import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

// ═══════════════════════════════════════════════════════════
// AbonnementContext — état d'abonnement partagé
// À placer dans src/context/
//
// Fournit :
//   - abonnement : { requis, actif, abonnement, gratuit }
//   - loading    : true tant que l'état n'est pas connu
//   - refresh()  : recharge l'état (après une souscription)
//
// « actif » signifie : accès autorisé (soit abonné, soit non requis).
// ═══════════════════════════════════════════════════════════

const AbonnementContext = createContext(null);

export const AbonnementProvider = ({ children }) => {
    const { user, loading: authLoading } = useAuth();
    const [etat, setEtat] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchEtat = useCallback(async () => {
        if (!user?.id) {
            setEtat(null);
            setLoading(false);
            return;
        }
        try {
            const response = await api.get('/abonnements/mon-abonnement');
            setEtat(response.data);
        } catch (err) {
            console.error('Erreur abonnement:', err);
            // En cas d'échec, on ne bloque pas l'utilisateur
            setEtat({ requis: false, actif: true, abonnement: null, gratuit: true });
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (!authLoading) fetchEtat();
    }, [authLoading, fetchEtat]);

    return (
        <AbonnementContext.Provider value={{ ...etat, loading, refresh: fetchEtat }}>
            {children}
        </AbonnementContext.Provider>
    );
};

export const useAbonnement = () => {
    const ctx = useContext(AbonnementContext);
    if (!ctx) {
        // Contexte absent : on renvoie un état neutre plutôt que de planter
        return { requis: false, actif: true, abonnement: null, gratuit: true, loading: false, refresh: () => { } };
    }
    return ctx;
};

export default AbonnementContext;